import React from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircle,
  faCopy,
  faPenToSquare,
  faShieldHalved,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import {
  actionTagClass,
  customerTagClass,
  describeRule,
  NOT_READ_BY_UNDERWRITING,
} from './blocksExplorerHelpers'
import { OP_LABELS, statusMeta } from './blockChangeHelpers'
import Loading from '../../../components/Loading'

const EFFECT_LABELS = {
  introduced: 'Introduced',
  overridden: 'Overridden',
  unchanged: 'No change',
  absent: 'Not present',
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'related', label: 'Related' },
  { key: 'history', label: 'History' },
]

export default function BlockDrawer({
  detail,
  isLoading,
  loadFailed,
  onRetry,
  actionLabels,
  onClose,
  onCopyRule,
  onEditRule = null,
  footer = null,
  tab,
  onTabChange: setTab,
}) {
  return (
    <aside
      className="lox-blocks-explorer__card lox-blocks-explorer__drawer"
      data-testid="block-drawer"
    >
      <div className="lox-blocks-explorer__drawer-top">
        <h3 className="title is-5 mb-0">Block details</h3>
        <button
          type="button"
          className="lox-blocks-explorer__icon-button"
          aria-label="Close"
          onClick={onClose}
          data-testid="block-drawer-close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {loadFailed && (
        <div
          className="has-text-centered p-4"
          data-testid="block-drawer-load-error"
        >
          <p className="has-text-danger mb-2">
            This block could not be loaded.
          </p>
          <button
            type="button"
            className="button is-small"
            onClick={onRetry}
            data-testid="block-drawer-retry"
          >
            Retry
          </button>
        </div>
      )}
      {!loadFailed && (isLoading || !detail) && <Loading noWrapper />}
      {!loadFailed && !isLoading && detail && (
        <BlockDrawerContent
          detail={detail}
          actionLabels={actionLabels}
          tab={tab}
          onTabChange={setTab}
          onCopyRule={onCopyRule}
          onEditRule={
            onEditRule
              ? () => {
                  setTab('overview')
                  onEditRule()
                }
              : null
          }
          footer={footer}
        />
      )}
    </aside>
  )
}

function relatedLevels(detail) {
  return detail.lineage.filter(
    (level) => level.row && level.row.id !== detail.row.id
  )
}

function BlockDrawerContent({
  detail,
  actionLabels,
  tab,
  onTabChange,
  onCopyRule,
  onEditRule,
  footer,
}) {
  const { table, row } = detail
  const actionLabel = (action) => actionLabels[action] ?? `Action ${action}`
  const isOverride =
    row.ancestor_id !== null &&
    row.ancestor_id !== undefined &&
    row.ancestor_id !== row.id
  const counts = {
    related: relatedLevels(detail).length,
    history: detail.history.length,
  }

  return (
    <>
      <div className="lox-blocks-explorer__drawer-head">
        <div>
          <span className="tag is-info is-light lox-no-border mb-1">
            {row.id < 0 ? `New row (temp:${-row.id})` : `#${row.id}`}
            {isOverride ? ` · overrides #${row.ancestor_id}` : ''}
          </span>
          <p className="has-text-weight-semibold lox-blocks-explorer__drawer-name">
            {row.name ?? <span className="has-text-grey">No name</span>}
          </p>
        </div>
        {onEditRule && (
          <button
            type="button"
            className="button is-small is-primary"
            onClick={onEditRule}
            data-testid="block-drawer-edit-rule"
          >
            <FontAwesomeIcon icon={faPenToSquare} className="mr-1" />
            Edit rule
          </button>
        )}
      </div>

      <div className="tabs is-small mb-3">
        <ul>
          {TABS.map(({ key, label }) => (
            <li key={key} className={tab === key ? 'is-active' : ''}>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault()
                  onTabChange(key)
                }}
                data-testid={`block-drawer-tab-${key}`}
              >
                {label}
                {key !== 'overview' && (
                  <span className="lox-blocks-explorer__chip-count">
                    {counts[key]}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {tab === 'overview' && (
        <OverviewTab
          detail={detail}
          actionLabel={actionLabel}
          isOverride={isOverride}
          onCopyRule={onCopyRule}
          footer={footer}
        />
      )}
      {tab === 'related' && (
        <div data-testid="block-drawer-related">
          {relatedLevels(detail).length === 0 ? (
            <p className="is-size-7 has-text-grey">
              No other rows share this block&apos;s slot.
            </p>
          ) : (
            relatedLevels(detail).map((level) => (
              <div key={level.treatment_id} className="lox-blocks-explorer__kv-row">
                <span>
                  #{level.row.id} ·{' '}
                  {level.treatment_name ?? `Treatment ${level.treatment_id}`}
                </span>
                <span>
                  <span
                    className={`tag lox-no-border mr-1 ${actionTagClass(
                      level.row.block_action
                    )}`}
                  >
                    {actionLabel(level.row.block_action)}
                  </span>
                  <span className="has-text-grey is-size-7">
                    {EFFECT_LABELS[level.effect] ?? level.effect}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>
      )}
      {tab === 'history' && (
        <div data-testid="block-drawer-history">
          {detail.history.length === 0 ? (
            <p className="is-size-7 has-text-grey">No change requests yet.</p>
          ) : (
            detail.history.map((entry) => (
              <div
                key={`${entry.change_request_id}-${entry.sequence}`}
                className="lox-blocks-explorer__history-entry"
              >
                <Link
                  to={`/lead_funnel_block_change_requests/${entry.change_request_id}`}
                >
                  CR-{entry.change_request_id} {entry.title}
                </Link>
                <p className="is-size-7 has-text-grey">
                  {OP_LABELS[entry.op] ?? entry.op} ·{' '}
                  {statusMeta(entry.status).label}
                  {entry.created_by_user_name
                    ? ` · ${entry.created_by_user_name}`
                    : ''}
                  {entry.applied_at ? ` · applied ${entry.applied_at}` : ''}
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </>
  )
}

function FindingList({ findings }) {
  if (findings.length === 0) {
    return <p className="is-size-7 has-text-grey">No findings.</p>
  }
  return findings.map((finding, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <p key={`${finding.code}-${index}`} className="is-size-7 mb-1">
      <span
        className={`tag lox-no-border mr-1 is-light ${
          finding.severity === 'error' ? 'is-danger' : 'is-warning'
        }`}
      >
        {finding.code}
      </span>
      {finding.message}
    </p>
  ))
}

function OverviewTab({ detail, actionLabel, isOverride, onCopyRule, footer }) {
  const { table, row } = detail
  const rule = describeRule({ table, row, conditions: detail.conditions })
  const sourceLevel = detail.lineage.find((level) => level.row?.id === row.id)
  let effect = 'not in effect here'
  if (detail.in_effect) effect = 'in effect'
  if (detail.dropped_by_customer_type) {
    effect = 'cut by the customer-type filter'
  }
  const isActive = row.active === 1
  const updatedAt = table === 'numeric' ? row.place_datetime : row.set_date

  return (
    <>
      <section className="lox-blocks-explorer__section">
        <h4 className="lox-blocks-explorer__section-title">
          {table === 'numeric' ? 'Rule' : 'Rule (all conditions)'}
        </h4>
        <div className="lox-blocks-explorer__rule-box">
          <code data-testid="block-drawer-rule">{rule}</code>
          <button
            type="button"
            className="lox-blocks-explorer__icon-button"
            aria-label="Copy rule"
            onClick={() => onCopyRule(rule)}
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>
        {table === 'categorical' && isOverride && (
          <p className="is-size-7 has-text-grey mt-1">
            These are #{row.ancestor_id}&apos;s conditions; an override has none
            of its own.
          </p>
        )}
        <dl className="lox-blocks-explorer__kv">
          <dt>Action</dt>
          <dd>
            <span
              className={`tag lox-no-border ${actionTagClass(
                row.block_action
              )}`}
            >
              {actionLabel(row.block_action)}
            </span>
          </dd>
          <dt>Customer</dt>
          <dd>
            <span
              className={`tag lox-no-border ${customerTagClass(
                row.customer_type
              )}`}
            >
              {row.customer_type ?? '—'}
            </span>
          </dd>
          <dt>Source</dt>
          <dd>
            <span className="tag is-light lox-no-border">
              {sourceLevel?.treatment_name ??
                (sourceLevel
                  ? `Treatment ${sourceLevel.treatment_id}`
                  : 'Not in this lineage')}
            </span>
          </dd>
          <dt>Table</dt>
          <dd>{table === 'numeric' ? 'Numeric' : 'Categorical'}</dd>
          <dt>Status</dt>
          <dd>
            <FontAwesomeIcon
              icon={faCircle}
              className={`mr-1 is-size-7 ${
                isActive ? 'has-text-success' : 'has-text-grey-light'
              }`}
            />
            {isActive ? 'Active' : 'Inactive'} · {effect}
          </dd>
          <dt>Placed</dt>
          <dd>{updatedAt ?? '—'}</dd>
        </dl>
      </section>

      <section className="lox-blocks-explorer__section">
        <h4 className="lox-blocks-explorer__section-title">Lineage</h4>
        <ol className="lox-blocks-explorer__lineage">
          {detail.lineage.map((level) => (
            <li
              key={level.treatment_id}
              data-testid="block-drawer-lineage-level"
              className={`is-${level.effect}`}
            >
              <strong>
                {level.treatment_name ?? `Treatment ${level.treatment_id}`}
              </strong>{' '}
              {level.row ? (
                <>
                  #{level.row.id}{' '}
                  <span
                    className={`tag lox-no-border ${actionTagClass(
                      level.row.block_action
                    )}`}
                  >
                    {actionLabel(level.row.block_action)}
                  </span>{' '}
                </>
              ) : null}
              <span className="has-text-grey">
                {EFFECT_LABELS[level.effect] ?? level.effect}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section className="lox-blocks-explorer__section">
        <h4
          className="lox-blocks-explorer__section-title"
          data-testid="block-drawer-findings-title"
        >
          <FontAwesomeIcon icon={faShieldHalved} className="mr-1" />
          Findings ({detail.findings.length})
        </h4>
        <div data-testid="block-drawer-findings">
          <FindingList findings={detail.findings} />
        </div>
      </section>

      {footer && (
        <section className="lox-blocks-explorer__section">{footer}</section>
      )}

      <section className="lox-blocks-explorer__section">
        <h4 className="lox-blocks-explorer__section-title">
          Not read by underwriting
        </h4>
        <dl
          className="lox-blocks-explorer__not-read is-size-7"
          data-testid="block-drawer-not-read"
        >
          {NOT_READ_BY_UNDERWRITING[table].map((column) => (
            <React.Fragment key={column}>
              <dt>{column}</dt>
              <dd>{row[column] ?? '—'}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>
    </>
  )
}
